import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSync } from '@/context/SyncContext';
import { sheetsService } from '@/services/sheets/sheetsService';
import { Button } from '@/components/common';
import { GOALS } from '@/constants';

// 32 frases motivacionales. Rota una por día (ciclo de 32 días).
const MOTIVATIONAL_PHRASES = [
  'Cada referido te acerca más a tu meta. ¡Tú puedes!',
  'El que captura hoy, cobra mañana.',
  'Un "no" te acerca al próximo "sí".',
  'La constancia le gana al talento.',
  'Tu actitud define tu resultado.',
  'Hoy es un gran día para hacer historia.',
  'Las metas grandes se logran un referido a la vez.',
  'No vendes productos, abres oportunidades.',
  'Tu mejor versión aparece cuando insistes.',
  'Quien siembra referidos, cosecha citas.',
  'Tu energía contagia confianza.',
  'El éxito ama a los que no se rinden.',
  'Cada visita es una semilla. Sigue sembrando.',
  'Lo que haces hoy construye tu mañana.',
  'Sonríe: la gente compra emociones, no cosas.',
  'La disciplina pesa onzas; el arrepentimiento pesa toneladas.',
  'Un buen referido vale más que mil excusas.',
  'Hazlo con pasión o no lo hagas.',
  'Tu próxima gran venta empieza con un saludo.',
  'Los campeones se hacen en los días difíciles.',
  'Confía en tu programa; el programa funciona.',
  'No cuentes los días, haz que los días cuenten.',
  'La excelencia es un hábito, no un accidente.',
  'Pide el referido; el peor "no" ya lo tienes.',
  'Cada llamada es una puerta. Tócalas todas.',
  'Tu meta no se cumple sola: muévete.',
  'Vender es ayudar. Ayuda más, vende más.',
  'La suerte llega a quien ya está trabajando.',
  'Un referido feliz trae cinco más.',
  'Tú no buscas clientes, creas relaciones.',
  'El momento perfecto es ahora.',
  'Cree, captura y conquista.',
];

// Devuelve la frase del día (una distinta cada día, rota las 32).
function getDailyPhrase(): string {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayNumber = Math.floor(startOfDay.getTime() / 86_400_000);
  return MOTIVATIONAL_PHRASES[dayNumber % MOTIVATIONAL_PHRASES.length];
}

export function HomePage() {
  const { user } = useAuth();
  const { pendingCount, isSyncing, syncNow } = useSync();
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0 });
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;

      try {
        const data = await sheetsService.getDashboardData(user.email, user.name);
        setStats({ today: data.today, week: data.week, month: data.month });
        setSpreadsheetId(data.spreadsheetId);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    };

    loadData();
  }, [user]);

  const sheetUrl = spreadsheetId
    ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit?usp=sharing`
    : null;

  const copyLink = useCallback(async () => {
    if (!sheetUrl) return;
    try {
      await navigator.clipboard.writeText(sheetUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = sheetUrl;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  }, [sheetUrl]);

  const todayProgress = Math.min((stats.today / GOALS.daily) * 100, 100);
  const dailyPhrase = getDailyPhrase();

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-6">
      {/* Welcome message */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-6 text-white">
        <p className="text-primary-100 text-sm">Bienvenido de vuelta,</p>
        <h2 className="text-2xl font-bold mb-4">{user?.name.split(' ')[0]}</h2>

        <div className="bg-white/10 rounded-xl p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-primary-100">Progreso de hoy</span>
            <span className="text-sm font-medium">
              {stats.today}/{GOALS.daily}
            </span>
          </div>
          <div className="h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${todayProgress}%` }}
            />
          </div>
          <p className="text-xs text-primary-100 mt-2">
            {stats.today >= GOALS.daily
              ? '¡Meta cumplida! Excelente trabajo.'
              : `Te faltan ${GOALS.daily - stats.today} referidos para tu meta diaria.`}
          </p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-4">
        <Link
          to="/captura"
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center gap-3 hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
          </div>
          <span className="font-medium text-gray-900">Nueva Captura</span>
        </Link>

        <Link
          to="/dashboard"
          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col items-center gap-3 hover:shadow-md transition-shadow"
        >
          <div className="w-12 h-12 bg-success-50 rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-success-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          </div>
          <span className="font-medium text-gray-900">Ver Stats</span>
        </Link>
      </div>

      {/* Stats summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <h3 className="font-semibold text-gray-900 mb-4">Resumen</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-primary-600">{stats.today}</p>
            <p className="text-xs text-gray-500">Hoy</p>
          </div>
          <div className="text-center border-x border-gray-100">
            <p className="text-2xl font-bold text-primary-600">{stats.week}</p>
            <p className="text-xs text-gray-500">Esta semana</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-primary-600">{stats.month}</p>
            <p className="text-xs text-gray-500">Este mes</p>
          </div>
        </div>
      </div>

      {/* Google Sheet link */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg className="w-5 h-5 text-green-600 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z" />
          </svg>
          <h3 className="font-semibold text-gray-900">Tu Base de Datos</h3>
        </div>

        {sheetUrl ? (
          <>
            <div className="flex items-center gap-2">
              <a
                href={sheetUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium text-sm py-2.5 px-4 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Abrir Google Sheet
              </a>
              <button
                onClick={copyLink}
                className="p-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                title="Copiar enlace"
              >
                {linkCopied ? (
                  <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
            {linkCopied && (
              <p className="text-xs text-green-600 mt-2 text-center font-medium">
                Enlace copiado
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500">
            Se creará automáticamente con tu primera captura
          </p>
        )}
      </div>

      {/* Sync status */}
      {pendingCount > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <svg
              className="w-5 h-5 text-warning-600 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-warning-800">
                {pendingCount} captura{pendingCount !== 1 ? 's' : ''} pendiente
                {pendingCount !== 1 ? 's' : ''} de sincronizar
              </p>
              <p className="text-xs text-warning-600">
                Se sincronizarán automáticamente cuando haya conexión
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={syncNow} loading={isSyncing}>
              Sincronizar
            </Button>
          </div>
        </div>
      )}

      {/* Motivational message */}
      <div className="bg-primary-50 rounded-xl p-4 text-center">
        <p className="text-primary-800 font-medium">
          {stats.today >= GOALS.daily
            ? '¡Eres un campeón! Sigue así.'
            : dailyPhrase}
        </p>
      </div>
    </div>
  );
}
